import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import {
  PrismButton,
  PrismSelectInput,
  PrismTooltip,
  PrismViewSwitcher,
} from '@mylighthouse/prism-ember';
import type { Option } from '@mylighthouse/prism-ember/types/components/prism/select-input';
import type { Task } from 'ember-concurrency';
import { t } from 'ember-intl';
import {
  BENI_STAY_DATE_SECTION_VIEWS,
  type BeniStayDateSectionView,
} from 'lumen/utils/constants/benchmark-insight/beni-constant-values';
import {
  DATE_AGGREGATION,
  type DateAggregationType,
} from 'lumen/utils/constants/general-constants';

type DateAggregationDropdownOption = Option & {
  id: DateAggregationType;
};

interface HotelBenchmarkInsightSectionsSectionStayDateSectionStayDateToolboxSignature {
  Args: {
    dateAggregation: DateAggregationType;
    exportStayDateTableTask: Task<void, []>;
    isLoading?: boolean;
    setDateAggregation: (dateAggregation: DateAggregationType) => void;
    setStayDateSectionView: (
      stayDateSectionView: BeniStayDateSectionView,
    ) => void;
    stayDateSectionView: BeniStayDateSectionView;
  };
}

export default class HotelBenchmarkInsightSectionsSectionStayDateSectionStayDateToolbox extends Component<HotelBenchmarkInsightSectionsSectionStayDateSectionStayDateToolboxSignature> {
  @service('benchmark-insight/features')
  declare benchmarkInsightFeatures: Services['benchmark-insight/features'];

  @service declare intl: Services['intl'];
  @service declare usageAnalytics: Services['usage-analytics'];

  get dateAggregationOptions(): DateAggregationDropdownOption[] {
    return [
      {
        id: DATE_AGGREGATION.DAY,
        label: this.intl.t('general.day').toString(),
      },
      {
        id: DATE_AGGREGATION.WEEK,
        label: this.intl.t('general.week').toString(),
      },
    ];
  }

  get isExportButtonDisabled(): boolean {
    const { isLoading, exportStayDateTableTask } = this.args;

    return (
      isLoading ||
      exportStayDateTableTask.isRunning ||
      this.benchmarkInsightFeatures.isBenchmarkInsightLight
    );
  }

  get selectedDateAggregation(): Option | undefined {
    return (
      this.dateAggregationOptions.find(
        (option) => option.id === this.args.dateAggregation,
      ) ?? undefined
    );
  }

  @action
  handleChangeDateAggregation(option: Option): void {
    const { dateAggregation, setDateAggregation } = this.args;
    const typedOption = option as DateAggregationDropdownOption;

    const newDateAggregation = typedOption.id;

    if (newDateAggregation === dateAggregation) {
      return;
    }

    setDateAggregation(newDateAggregation);

    this.usageAnalytics.track('Time Granularity Changed', {
      eventTrigger: 'performance-overview',
      product: 'benchmark-insight',
      from: dateAggregation,
      to: newDateAggregation,
    });
  }

  @action
  handleClickExport(): void {
    void this.args.exportStayDateTableTask.perform();

    this.usageAnalytics.track('Table Exported', {
      eventTrigger: 'performance-overview',
      product: 'benchmark-insight',
    });
  }

  @action
  handleStayDateSectionViewChange(newStayDateSectionView: string): void {
    const { stayDateSectionView, setStayDateSectionView } = this.args;

    if (newStayDateSectionView === stayDateSectionView) {
      return;
    }

    setStayDateSectionView(newStayDateSectionView as BeniStayDateSectionView);

    this.usageAnalytics.track('Data Visualisation Changed', {
      eventTrigger: 'performance-overview',
      product: 'benchmark-insight',
      from: this.args.stayDateSectionView,
      to: newStayDateSectionView,
    });
  }

  <template>
    <div class="flex flex-row items-center gap-400">
      <div class="flex items-center">
        <p class="text-300--regular mr-200">{{t "parity.view_by"}}</p>
        <PrismSelectInput
          @onChange={{this.handleChangeDateAggregation}}
          @options={{this.dateAggregationOptions}}
          @value={{this.selectedDateAggregation}}
        />
      </div>

      <PrismViewSwitcher
        @activeButtonId={{@stayDateSectionView}}
        @onSetActiveButtonId={{this.handleStayDateSectionViewChange}}
        as |ViewSwitcher|
      >
        <ViewSwitcher.button
          @icon="view-chart"
          @id={{BENI_STAY_DATE_SECTION_VIEWS.CHART}}
          @label=""
          data-test-id="toolbox-stay-date-section-chart-view-button"
        />
        <ViewSwitcher.button
          @icon="view-table"
          @id={{BENI_STAY_DATE_SECTION_VIEWS.TABLE}}
          @label=""
          data-test-id="toolbox-stay-date-section-table-view-button"
        />
      </PrismViewSwitcher>

      {{#if this.benchmarkInsightFeatures.showExportButtons}}
        <div>
          <PrismButton
            @buttonType="secondary"
            @iconBefore="export"
            @isDisabled={{this.isExportButtonDisabled}}
            @isLoading={{@exportStayDateTableTask.isRunning}}
            @label={{t "general.export_group.export"}}
            @onClick={{this.handleClickExport}}
            class="text-nowrap"
            data-test-id="toolbox-stay-date-section-export-button"
          />

          {{#if this.benchmarkInsightFeatures.isBenchmarkInsightLight}}
            <PrismTooltip
              @text={{this.benchmarkInsightFeatures.beniLightTooltipText}}
            />
          {{/if}}
        </div>
      {{/if}}
    </div>
  </template>
}
